// ─────────────────────────────────────────────────────────
// store/dataStore.ts — All data collection slices (Supabase)
// Replaces: the global D object from the monolith
// Each collection is a typed array; cleared on logout.
// ─────────────────────────────────────────────────────────
import { create } from 'zustand'
import type {
  Project, BOQItem, Milestone, DPREntry, Tender,
  EMD, Receivable, RetentionRelease, Expense, CashFlowEntry,
  PettyCashEntry, BankReconEntry, Vendor, PurchaseOrder,
  Quotation, ContractorInvoice, Employee, AttSheet,
  SalaryRecord, Advance, Insurance, TDSEntry, EInvoice,
  Task, AuditEntry, Notification,
} from '@/types'

// Attendance is keyed by month key (YYYY-M) → AttSheet
type AttMap = Record<string, AttSheet>

// Salary is keyed by sk (YYYY-M-empId) → SalaryRecord
type SalMap = Record<string, SalaryRecord>

interface DataState {
  // ── Collections ─────────────────────────────────────
  proj:        Project[]
  boq:         BOQItem[]
  milestones:  Milestone[]
  daily:       DPREntry[]
  tender:      Tender[]
  emd:         EMD[]
  recv:        Receivable[]
  ret:         RetentionRelease[]
  exp:         Expense[]
  cf:          CashFlowEntry[]
  petty:       PettyCashEntry[]
  recon:       BankReconEntry[]
  vendor:      Vendor[]
  po:          PurchaseOrder[]
  quotations:  Quotation[]
  invoice:     ContractorInvoice[]
  emp:         Employee[]
  att:         AttMap
  sal:         SalMap
  adv:         Advance[]
  ins:         Insurance[]
  tds:         TDSEntry[]
  einvoices:   EInvoice[]
  task:        Task[]
  audit:       AuditEntry[]
  notif:       Notification[]

  // ── Setters (one per collection) ─────────────────────
  setProj:       (data: Project[])           => void
  setBoq:        (data: BOQItem[])           => void
  setMilestones: (data: Milestone[])         => void
  setDaily:      (data: DPREntry[])          => void
  setTender:     (data: Tender[])            => void
  setEmd:        (data: EMD[])               => void
  setRecv:       (data: Receivable[])        => void
  setRet:        (data: RetentionRelease[])  => void
  setExp:        (data: Expense[])           => void
  setCf:         (data: CashFlowEntry[])     => void
  setPetty:      (data: PettyCashEntry[])    => void
  setRecon:      (data: BankReconEntry[])    => void
  setVendor:     (data: Vendor[])            => void
  setPo:         (data: PurchaseOrder[])     => void
  setQuotations: (data: Quotation[])         => void
  setInvoice:    (data: ContractorInvoice[]) => void
  setEmp:        (data: Employee[])          => void
  setAtt:        (key: string, data: AttSheet) => void
  setAttAll:     (data: Record<string, AttSheet>) => void
  setSal:        (key: string, data: SalaryRecord) => void
  setAdv:        (data: Advance[])           => void
  setIns:        (data: Insurance[])         => void
  setTds:        (data: TDSEntry[])          => void
  setEinvoices:  (data: EInvoice[])          => void
  setTask:       (data: Task[])              => void
  setAudit:      (data: AuditEntry[])        => void
  setNotif:      (data: Notification[])      => void

  // ── Clear all (called on logout) ─────────────────────
  clearAll: () => void
}

const EMPTY_STATE = {
  proj: [], boq: [], milestones: [], daily: [], tender: [],
  emd: [], recv: [], ret: [], exp: [], cf: [], petty: [],
  recon: [], vendor: [], po: [], quotations: [], invoice: [],
  emp: [], att: {}, sal: {}, adv: [], ins: [], tds: [],
  einvoices: [], task: [], audit: [], notif: [],
}

export const useDataStore = create<DataState>((set) => ({
  ...EMPTY_STATE,

  setProj:       (proj)       => set({ proj }),
  setBoq:        (boq)        => set({ boq }),
  setMilestones: (milestones) => set({ milestones }),
  setDaily:      (daily)      => set({ daily }),
  setTender:     (tender)     => set({ tender }),
  setEmd:        (emd)        => set({ emd }),
  setRecv:       (recv)       => set({ recv }),
  setRet:        (ret)        => set({ ret }),
  setExp:        (exp)        => set({ exp }),
  setCf:         (cf)         => set({ cf }),
  setPetty:      (petty)      => set({ petty }),
  setRecon:      (recon)      => set({ recon }),
  setVendor:     (vendor)     => set({ vendor }),
  setPo:         (po)         => set({ po }),
  setQuotations: (quotations) => set({ quotations }),
  setInvoice:    (invoice)    => set({ invoice }),
  setEmp:        (emp)        => set({ emp }),
  setAtt:        (key, data)  => set((s) => ({ att: { ...s.att, [key]: data } })),
  setAttAll:     (data)       => set({ att: data }),
  setSal:        (key, data)  => set((s) => ({ sal: { ...s.sal, [key]: data } })),
  setAdv:        (adv)        => set({ adv }),
  setIns:        (ins)        => set({ ins }),
  setTds:        (tds)        => set({ tds }),
  setEinvoices:  (einvoices)  => set({ einvoices }),
  setTask:       (task)       => set({ task }),
  setAudit:      (audit)      => set({ audit }),
  setNotif:      (notif)      => set({ notif }),

  clearAll: () => set(EMPTY_STATE),
}))
